import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { updateOrderDates } from "@/lib/ordersApi";
import { getApiErrorMessage } from "@/lib/api";
import { orderTitle } from "@/lib/orderUtils";
import type { OrderDetail, OrderSummary } from "@/lib/types";

const FIELDS = [
  { key: "purchaseStartedAt", label: "Em compra" },
  { key: "productionScheduledAt", label: "Em produção" },
  { key: "expeditionScheduledAt", label: "Em expedição" },
  { key: "expeditionReleasedAt", label: "Saída para entrega" },
] as const;

// Mesmos presets da criação do pedido (Checkout). "Personalizado" libera um horário manual.
const PERIOD_PRESETS = ["Manhã (06h - 10h)", "Tarde (13h - 17h)", "Madrugada (00h - 05h)"];
const CUSTOM_PERIOD = "__custom__";

/** Extrai o período (texto após a data) de uma janela "DD/MM/YYYY · período". */
function parsePeriod(window?: string | null): string {
  if (!window) return "";
  const trimmed = window.trim();
  const m = trimmed.match(/^(\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2})\s*(·\s*)?/);
  return m ? trimmed.slice(m[0].length).trim() : trimmed;
}

interface Props {
  order: OrderSummary | null;
  onClose: () => void;
  onSaved?: (order: OrderDetail) => void;
}

/** Janela dedicada (acessada pelo menu do card) para ajustar as datas do pedido. */
export function OrderDatesDialog({ order, onClose, onSaved }: Props) {
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [deliveryDate, setDeliveryDate] = useState("");
  const [period, setPeriod] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const [saving, setSaving] = useState(false);

  const originalDeliveryDate = (order?.deliveryDate ?? "").slice(0, 10);
  const originalPeriod = parsePeriod(order?.deliveryWindow);

  useEffect(() => {
    const parsed = parsePeriod(order?.deliveryWindow);
    setEdits({});
    setDeliveryDate((order?.deliveryDate ?? "").slice(0, 10));
    setPeriod(parsed);
    setCustomMode(parsed !== "" && !PERIOD_PRESETS.includes(parsed));
  }, [order?.id, order?.deliveryDate, order?.deliveryWindow]);

  const stageRows = order ? FIELDS.filter((f) => !!order[f.key]) : [];
  const deliveryChanged = deliveryDate !== originalDeliveryDate || period !== originalPeriod;
  const hasChanges = deliveryChanged || Object.keys(edits).length > 0;

  const save = async () => {
    if (!order || !hasChanges) return;
    setSaving(true);
    try {
      const payload: Parameters<typeof updateOrderDates>[1] = { ...edits };
      if (deliveryChanged && deliveryDate) {
        payload.deliveryWindow = period.trim() ? `${deliveryDate} · ${period.trim()}` : deliveryDate;
      }
      const updated = await updateOrderDates(order.id, payload);
      onSaved?.(updated);
      toast.success("Datas atualizadas");
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível atualizar as datas"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={order != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4" /> Datas do pedido
          </DialogTitle>
          <DialogDescription>{order ? orderTitle(order) : ""}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2.5 py-1">
          <div className="flex items-center justify-between gap-3">
            <span className="shrink-0 text-sm font-medium text-foreground/80">Data de entrega</span>
            <DatePicker
              className="w-44"
              value={deliveryDate}
              onChange={(next) => setDeliveryDate(next)}
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="shrink-0 text-sm text-muted-foreground">Período</span>
            <Select
              value={customMode ? CUSTOM_PERIOD : period || undefined}
              onValueChange={(value) => {
                if (value === CUSTOM_PERIOD) {
                  setCustomMode(true);
                } else {
                  setCustomMode(false);
                  setPeriod(value);
                }
              }}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_PRESETS.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
                <SelectItem value={CUSTOM_PERIOD}>Personalizado…</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {customMode && (
            <Input
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              placeholder="ex.: 08h às 10h ou das 13h às 15h"
              autoFocus
            />
          )}

          {stageRows.length > 0 && <div className="border-t border-border/60" />}

          {stageRows.map((f) => (
            <div key={f.key} className="flex items-center justify-between gap-3">
              <span className="shrink-0 text-sm font-medium text-foreground/80">{f.label}</span>
              <DatePicker
                className="w-44"
                value={edits[f.key] ?? (order?.[f.key] ?? "").slice(0, 10)}
                onChange={(next) => setEdits((prev) => ({ ...prev, [f.key]: next }))}
              />
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button size="sm" onClick={() => void save()} disabled={saving || !hasChanges}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Salvar datas
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
